import React, { useState } from 'react';
import ProductSelectionModal from '../../new-order-components/ProductSelectionModal';

const AddPurchaseItemsTable = ({ 
  items = [],  
  onAddItem, 
  onUpdateItem, 
  onRemoveItem,
  totals = {},
  onUpdateTotals = () => {}
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  // Safe format numbers
  const safeFormat = (value) => {
    const num = parseFloat(value);
    return isNaN(num) ? '0.00' : num.toFixed(2);
  };
  
  // Calculate total HT for all items
  const calculateTotalHT = (items) => {
    if (!items || items.length === 0) return 0;
    return items.reduce((sum, item) => {
      // Make sure we handle missing values
      const price = parseFloat(item.price) || 0;
      const quantity = parseInt(item.quantity, 10) || 0;
      
      // Calculate the total for this item (no remise in calculation)
      const itemTotal = price * quantity;
      return sum + itemTotal;
    }, 0);
  };
  
  const totalHT = calculateTotalHT(items);
  
  // Update the handleProductSelect function to batch add products
  const handleProductSelect = (productData) => {
    console.log("Selected product data:", productData);
    
    // Check if productData is an array
    if (Array.isArray(productData)) {
      console.log("Processing multiple products:", productData.length);
      
      // Process all products into items array first
      const newItems = productData.map(product => {
        console.log("Processing product for batch add:", product);
        
        return {
          id: product.variantId || product.id || Math.random().toString(36).substr(2, 9),
          productId: product.productId || product.id,
          variantId: product.variantId || null,
          name: product.name || "Produit sans nom",
          reference: product.reference || product.variant?.reference || "Sans référence",
          prix: parseFloat(product.prixVente || product.price || 0),
          price: parseFloat(product.prixVente || product.price || 0),
          quantity: product.quantity || 1, // ✅ PRESERVE the quantity from modal
          tva: 19,
          taxRate: 19,
          remise: 0,
          discount: 0,
          stock: parseInt(product.stock || 0, 10),
          isVariant: Boolean(product.isVariant)
        };
      });
      
      console.log("Batch adding items:", newItems);
      
      // Add all items at once
      newItems.forEach(item => {
        console.log("Adding item from batch with quantity:", item.quantity);
        onAddItem(item);
      });
      
    } else {
      // Handle single product
      console.log("Adding single product with quantity:", productData.quantity);
      addSingleProduct(productData);
    }
    
    // Close the modal
    setIsModalOpen(false);
  };

  // Also fix the addSingleProduct function:
  const addSingleProduct = (product) => {
    if (!product) {
      console.error("Invalid product data");
      return;
    }
    
    console.log("Processing single product with quantity:", product.quantity);
    
    const newItem = {
      id: product.variantId || product.id || Math.random().toString(36).substr(2, 9),
      productId: product.productId || product.id,
      variantId: product.variantId || null,
      name: product.name || "Produit sans nom",
      reference: product.reference || product.variant?.reference || "Sans référence",
      prix: parseFloat(product.prixVente || product.price || 0),
      price: parseFloat(product.prixVente || product.price || 0),
      quantity: product.quantity || 1, // ✅ PRESERVE the quantity from modal
      tva: 19,
      taxRate: 19,
      remise: 0,
      discount: 0,
      stock: parseInt(product.stock || 0, 10),
      isVariant: Boolean(product.isVariant)
    };
    
    console.log("Adding single item with preserved quantity:", newItem.quantity);
    onAddItem(newItem);
  };
  
  // Update the handleItemChange function to properly handle field updates
  const handleItemChange = (id, field, value) => {
    console.log(`Changing ${field} for item ${id} to ${value}`);
    
    // For quantities, ensure we have a valid integer
    if (field === 'quantity') {
      // Parse as integer, defaulting to 1 if conversion fails
      value = parseInt(value, 10) || 1;
    } 
    // For price and other numeric fields, ensure we have a valid number
    else if (['price', 'tva'].includes(field)) {
      // Parse as float, defaulting to 0 if conversion fails
      value = parseFloat(value) || 0;
    }

    // Create an update object with just the changed field
    const updateData = { [field]: value };
    console.log("Sending update:", updateData);
    
    // Call the update handler with the ID and update object
    onUpdateItem(id, updateData);
  };

  return (
    <div className="grid grid-cols-1 gap-6">
      <div className="lg:col-span-2">
        <div className="bg-white shadow-sm rounded-lg border border-gray-200 overflow-hidden">
          <div className="px-6 py-5 sm:px-8 bg-white border-b border-gray-200">
            <div className="flex justify-between items-center">
              <div className="flex items-center">
                <div className="p-2 rounded-md bg-[#f5f8f7] mr-4">
                  <svg className="h-6 w-6 text-[#174540]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg leading-6 font-medium text-gray-900">Articles de l'achat</h3>
                  <p className="mt-1 max-w-2xl text-sm text-gray-500">
                    Détails des produits et services associés à cet achat
                  </p>
                </div>
              </div>
            </div>
          </div>
          
          {(!items || items.length === 0) ? (
            <div className="flex flex-col items-center justify-center py-14 px-4 sm:px-6 border-t border-gray-200 bg-gray-50">
              <div className="p-3 rounded-md bg-[#f5f8f7] mb-4">
                <svg className="h-12 w-12 text-[#174540]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                </svg>
              </div>
              <h3 className="mt-2 text-lg font-medium text-gray-900">Aucun article dans l'achat</h3>
              <p className="mt-2 text-center text-sm text-gray-500 max-w-md">
                Ajoutez des produits ou services à cet achat pour les voir apparaître ici.
              </p>
              <div className="mt-6">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(true)}
                  className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-[#174540] hover:bg-[#0d2926] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#174540] transition-all duration-200"
                >
                  <svg className="mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
                  </svg>
                  Ajouter un nouvel article
                </button>
              </div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Produit
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Référence
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Prix unitaire
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Quantité
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      TVA (%)
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Total HT
                    </th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {items.map((item) => {
                    // Calculate HT price and total
                    const price = parseFloat(item.price) || 0;
                    const quantity = parseInt(item.quantity, 10) || 1;
                    const totalHtPrice = price * quantity;
                    
                    return (
                      <tr key={item.id}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {item.name}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {item.reference}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={item.price}
                            disabled={true}
                            readOnly={true}
                            className="shadow-sm block w-28 border border-gray-200 bg-gray-50 text-gray-500 rounded-md py-1.5 px-2 text-sm cursor-not-allowed"
                          />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <input
                            type="number"
                            min="1"
                            value={item.quantity}
                            onChange={(e) => handleItemChange(item.id, 'quantity', e.target.value)}
                            className="shadow-sm block w-20 border border-gray-300 rounded-md py-1.5 px-2 text-sm focus:ring-[#174540] focus:border-[#174540] transition-colors duration-200"
                          />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <input
                            type="number"
                            min="0"
                            max="100"
                            value={item.tva}
                            disabled={true}
                            readOnly={true}
                            className="shadow-sm block w-20 border border-gray-200 bg-gray-50 text-gray-500 rounded-md py-1.5 px-2 text-sm cursor-not-allowed"
                          />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-medium">
                          {safeFormat(totalHtPrice)} TND
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <button
                            onClick={() => onRemoveItem(item.id)}
                            className="text-red-600 hover:text-red-900 focus:outline-none focus:underline transition-colors duration-200"
                            title="Supprimer cet article"
                          >
                            Supprimer
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          
          {items && items.length > 0 && (
            <div className="border-t border-gray-200 px-6 py-4 sm:px-8 bg-gray-50">
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center">
                <div className="text-sm text-gray-500 mb-3 sm:mb-0">
                  {items.length} article{items.length > 1 ? 's' : ''} dans l'achat
                </div>
                <div className="flex items-center">
                  <div className="mr-6 text-sm">
                    <span className="font-medium">Total HT:</span> {safeFormat(totalHT)} TND
                  </div>
                  <button
                    type="button"
                    onClick={() => setIsModalOpen(true)}
                    className="inline-flex items-center px-3 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-[#174540] hover:bg-[#0d2926] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#174540]"
                  >
                    <svg className="mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
                    </svg>
                    Ajouter plus d'articles
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      
      <ProductSelectionModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onProductSelect={handleProductSelect}
        allowOutOfStock={true} // Add this to allow out-of-stock item selection
        purchaseMode={true} // Use purchase price (prixAchat) for purchase orders
      />
    </div>
  );
};

export default AddPurchaseItemsTable;